import React, { useState, useEffect, useRef } from 'react';

interface AnimatedCounterProps {
  value: string | number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 2000,
  prefix = '',
  suffix = ''
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Extract numeric value from string or number
  const extractNumber = (val: string | number): number => {
    if (typeof val === 'number') return val;
    
    // Handle percentages like "99.9%" -> 99.9
    const percentMatch = val.toString().match(/^(\d+\.?\d*)%$/);
    if (percentMatch) return parseFloat(percentMatch[1]);
    
    // Handle values like "10s" -> 10
    const numberMatch = val.toString().match(/^(\d+\.?\d*)/);
    if (numberMatch) return parseFloat(numberMatch[1]);
    
    // Handle "10-10K+" type -> take first number
    const rangeMatch = val.toString().match(/^(\d+\.?\d*)-/);
    if (rangeMatch) return parseFloat(rangeMatch[1]);
    
    return 0;
  };

  const numericValue = extractNumber(value);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isVisible || numericValue === 0) return;

    let startTime: number | null = null;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = (x: number) => 1 - Math.pow(1 - x, 4);
      const easedProgress = easeOutQuart(progress);
      
      const newValue = easedProgress * numericValue;
      setDisplayValue(newValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, numericValue, duration]);

  // Determine original format to preserve
  const hasPercentSign = typeof value === 'string' && value.includes('%');
  const hasSuffix = typeof value === 'string' && /\d+s$/.test(value);
  const formatNumber = (num: number) => {
    if (hasPercentSign) {
      return `${prefix}${num.toFixed(1)}%`;
    }
    if (hasSuffix) {
      return `${prefix}${Math.round(num)}s`;
    }
    return `${prefix}${num.toFixed(0)}${suffix}`;
  };

  return (
    <span ref={elementRef} className="tabular-nums">
      {formatNumber(displayValue)}
    </span>
  );
};

export default AnimatedCounter;
